import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { Link, useSearchParams, useLocation } from "react-router-dom";
import { 
  Plus, X, Trash2, 
  Edit2, Check, GripVertical, Settings,
  Undo2, Play, Cloud, CloudOff, LogOut, Search, Menu, Home, Share, Share2, RotateCcw,
  Zap, Mail, Phone, Printer, Star, Copy, Twitter, Linkedin, Facebook, Bell, BellOff, Lock, LockOpen
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import {
  DndContext,
  DragOverlay,
  closestCorners,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  useDroppable,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  horizontalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  restrictToHorizontalAxis,
  restrictToParentElement,
} from "@dnd-kit/modifiers";
import { v4 as uuidv4 } from "uuid";
import { useBoardData, detectCategory } from "@/hooks/useBoardData";
import { ResetModal } from "@/components/app/ResetModal";
import { LogoIcon } from "@/components/Logo";
import { CompletionCelebration } from "@/components/app/CompletionCelebration";
import { QuarterMarkModal } from "@/components/app/QuarterMarkModal";
import { EarlyMomentumModal } from "@/components/app/EarlyMomentumModal";
import { GoalCompletionModal } from "@/components/app/GoalCompletionModal";
import { CycleCompletionModal } from "@/components/app/CycleCompletionModal";
import { MilestoneSharePanel } from "@/components/app/MilestoneSharePanel";
import { useSoundSettings } from "@/components/app/SoundManager";
import { SettingsPanel, useAppSettings } from "@/components/app/SettingsPanel";
import { useStreak, StreakBadge, StreakPanel, MilestoneCelebration } from "@/components/app/StreakCounter";
import { DailyCheckIn } from "@/components/app/DailyCheckIn";
import { DailySystemTask, triggerFirstTaskAdded } from "@/components/app/DailySystemTask";
import { MorningThread, useMorningThread } from "@/components/app/MorningThread";
import { DailyMovementTask } from "@/components/app/DailyMovementTask";
import { DragHint } from "@/components/app/DragHint";
import { SyncInvitation, incrementTaskCount, trackEngagement } from "@/components/app/SyncInvitation";
import { SignInReminder } from "@/components/app/SignInReminder";
import CompletionAcknowledgment from "@/components/app/CompletionAcknowledgment";
import { ActionGuide } from "@/components/app/ActionGuide";
import SaveStatusIndicator from "@/components/app/SaveStatusIndicator";
import InstallPrompt, { triggerInstallPrompt, INSTALL_PROMPT_EVENTS, trackTaskCompletion } from "@/components/app/InstallPrompt";
import ReminderOptIn, { shouldShowReminderOptIn } from "@/components/app/ReminderOptIn";
import { MobileFooter, DesktopFooterButtons } from "@/components/app/MobileFooter";
import PrintView from "@/components/app/PrintView";
import ShareTaskModal from "@/components/app/ShareTaskModal";
import { useCurrentThread } from "@/hooks/useCurrentThread";
import { useInactivityCues } from "@/hooks/useInactivityCues";
import { useNudgeSystem } from "@/hooks/useNudgeSystem";
import { useLockScreenFocus } from "@/hooks/useLockScreenFocus";
import { LockScreenFocusModal } from "@/components/app/LockScreenFocusModal";
import { NudgePermissionModal } from "@/components/app/NudgePermissionModal";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { EMAIL_NUDGES_ENABLED } from "@/config/featureFlags";
import { playHeartbeatSound, playFocusSwooshSound, playThumpWithSwoosh, playLockScreenPinSound, playLockScreenReplaceSound } from "@/utils/sounds";

// Organic Highlighter Component - creates a natural, hand-drawn highlighter effect
import {
  SortableCard,
  CardOverlay,
  DroppableList,
  ColumnGripHandle,
  SortableColumn,
  ListColumn,
} from "./appPage/BoardComponents";
import {
  CardEditModal,
  ShareAppModal,
  FindThreadRitual,
} from "./appPage/Modals";

// LocalStorage key
const STORAGE_KEY = "wherewasi_data";

// LocalStorage key for last active task (used by /now page)
const LAST_ACTIVE_KEY = 'wwi_last_active_task';

// Save last active task to localStorage for /now page instant retrieval
const saveLastActiveTask = (task, listName) => {
  if (!task || !task.id) return;
  try {
    const taskData = {
      taskId: task.id,
      content: task.content,
      category: task.category || 'action',
      listName: listName || 'Doing',
      timestamp: task.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    localStorage.setItem(LAST_ACTIVE_KEY, JSON.stringify(taskData));
  } catch (e) {
    console.error('Error saving last active task:', e);
  }
};

// Demo data with varied ages to show thread aging indicators
const getDemoData = () => {
  const now = new Date();
  const daysAgo = (days) => new Date(now - days * 24 * 60 * 60 * 1000).toISOString();

  return {
    boards: [{
      id: "demo-board",
      name: "My Focus Board",
      lists: [
        {
          id: "list-1",
          name: "To Do",
          cards: [
            { id: "card-1", content: "Finish writing that email to Sarah", notes: "", createdAt: daysAgo(0), isCritical: true, category: "email" },
            { id: "card-2", content: "Review project proposal", notes: "Due by Friday", createdAt: daysAgo(2), isCritical: false },
          ]
        },
        {
          id: "list-2",
          name: "Doing",
          cards: [
            { id: "card-3", content: "Research competitor features", notes: "Started recently", createdAt: daysAgo(1), isCritical: false, category: "action" },
            { id: "card-5", content: "Draft weekly rhythm plan", notes: "Taking time, and that's okay", createdAt: daysAgo(5), isCritical: true, category: "action" },
            { id: "card-6", content: "Email Sarah about project update", notes: "", createdAt: daysAgo(2), isCritical: false, category: "email" },
            { id: "card-7", content: "Call accountant re: invoices", notes: "", createdAt: daysAgo(3), isCritical: false, category: "phone" },
          ]
        },
        {
          id: "list-3",
          name: "Done",
          cards: [
            { id: "card-4", content: "Set up meeting with team", createdAt: daysAgo(3), completedAt: daysAgo(1), isCritical: false },
          ]
        }
      ]
    }],
    currentBoardId: "demo-board"
  };
};

const getEmptyData = () => ({
  boards: [{
    id: uuidv4(),
    name: "My Focus Board",
    lists: [
      { id: uuidv4(), name: "To Do", cards: [] },
      { id: uuidv4(), name: "Doing", cards: [] },
      { id: uuidv4(), name: "Done", cards: [] }
    ]
  }],
  currentBoardId: null
});

// Helper to process board data - migrate old column names
const processListData = (data) => {
  if (!data?.boards) return data;

  data.boards.forEach(board => {
    if (board.lists) {
      board.lists.forEach(list => {
        if (list.name?.toLowerCase() === "in progress") {
          list.name = "DOING";
        }
      });
    }
  });

  return data;
};

const triggerHaptic = (pattern = 'success') => {
  if ('vibrate' in navigator) {
    switch (pattern) {
      case 'success':
        navigator.vibrate(45);
        break;
      case 'light':
        navigator.vibrate(25);
        break;
      case 'double':
        navigator.vibrate([30, 60, 30]);
        break;
      case 'heartbeat':
        navigator.vibrate([50, 150, 40]);
        break;
      default:
        navigator.vibrate(45);
    }
  }
};

// Heartbeat haptic with precise timing for completion
const triggerHeartbeatHaptic = (isMilestone = false, milestoneCount = 0) => {
  playHeartbeatSound(isMilestone, milestoneCount);

  const isMobileDevice = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  if (isMobileDevice && 'vibrate' in navigator) {
    if (isMilestone) {
      navigator.vibrate(120);
      setTimeout(() => navigator.vibrate(50), 650);
    } else {
      navigator.vibrate(100);
      setTimeout(() => navigator.vibrate(45), 580);
    }
  }
};

// Milestone heartbeat - deeper, more grounded
const triggerMilestoneHaptic = (milestoneCount = 0) => {
  triggerHeartbeatHaptic(true, milestoneCount);
};

export default function AppPage() {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const isDemo = searchParams.get("demo") === "1";
  const focusTaskId = searchParams.get("focus"); // From /now page navigation

  // Nudge email verification magic-link redirect handler
  useEffect(() => {
    const status = searchParams.get("nudge_email_verified");
    if (!status) return;
    const messages = {
      "1": { kind: "success", text: "Nudge address confirmed — you're all set." },
      "used": { kind: "info", text: "This address was already confirmed." },
      "expired": { kind: "error", text: "That confirmation link has expired. Save the address again to get a fresh link." },
      "invalid": { kind: "error", text: "That confirmation link isn't valid." },
    };
    const msg = messages[status];
    if (msg) {
      // Defer by a tick — <Toaster/> mounts as a sibling AFTER <AppRouter/>,
      // so a synchronous toast() here fires before sonner has subscribed and
      // gets dropped. requestAnimationFrame + a short delay lets Toaster
      // subscribe first.
      requestAnimationFrame(() => setTimeout(() => {
        if (msg.kind === "success") toast.success(msg.text);
        else if (msg.kind === "error") toast.error(msg.text);
        else toast(msg.text);
      }, 200));
    }
    // Strip the query param so a refresh doesn't re-toast.
    const clean = new URL(window.location.href);
    clean.searchParams.delete("nudge_email_verified");
    window.history.replaceState({}, "", clean.pathname + clean.search + clean.hash);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  // Board data state - managed locally (useBoardData hook available for future refactoring)
  const [data, setData] = useState(null);
  const [syncStatus, setSyncStatus] = useState('idle'); // idle, syncing, synced, error
  const [isLoadingCloud, setIsLoadingCloud] = useState(false);
  
  const [showResetModal, setShowResetModal] = useState(false);
  const [recentlyReset, setRecentlyReset] = useState(false); // For warm fog after reset
  const [editingCard, setEditingCard] = useState(null);
  const [activeCard, setActiveCard] = useState(null);
  const [activeColumn, setActiveColumn] = useState(null);
  const [isAddingList, setIsAddingList] = useState(false);
  const [newListName, setNewListName] = useState("");
  const [showSettings, setShowSettings] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);
  const [celebrationOrigin, setCelebrationOrigin] = useState(null); // {x, y} position of completed task
  const [showStreakPanel, setShowStreakPanel] = useState(false);
  const [showQuarterMarkModal, setShowQuarterMarkModal] = useState(false);
  const [showEarlyMomentumModal, setShowEarlyMomentumModal] = useState(false);
  const [showGoalCompletionModal, setShowGoalCompletionModal] = useState(false);
  const [showCycleCompletionModal, setShowCycleCompletionModal] = useState(false);
  const [showPrintView, setShowPrintView] = useState(false);
  
  // Post-milestone share panel state
  const [showMilestoneSharePanel, setShowMilestoneSharePanel] = useState(false);
  const [milestoneShareValue, setMilestoneShareValue] = useState(null);
  const [sharedMilestones, setSharedMilestones] = useState(new Set()); // Track which milestones have been offered
  
  // Find thread ritual state
  const [showFindThreadRitual, setShowFindThreadRitual] = useState(false);
  const [ritualTaskName, setRitualTaskName] = useState(null);
  
  // Share task modal state
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareTask, setShareTask] = useState(null);
  
  // Share app button state (dismissible)
  const [showShareAppButton, setShowShareAppButton] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('wwi_share_app_dismissed') !== 'true';
    }
    return true;
  });
  
  // Share app modal state (for desktop)
  const [showShareAppModal, setShowShareAppModal] = useState(false);
  
  // Session-based completion tracking for acknowledgment system
  const [sessionCompletedCount, setSessionCompletedCount] = useState(0);
  const [sessionMilestonesHit, setSessionMilestonesHit] = useState([]);
  
  // Search state
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const searchInputRef = useRef(null);

  // Daily Focus View - hides non-Important tasks in To Do/Done to reduce
  // visual noise. Doing column always stays fully visible (current work).
  const [dailyFocus, setDailyFocus] = useState(() => {
    if (typeof window === "undefined") return false;
    return localStorage.getItem("wwi_daily_focus") === "true";
  });
  useEffect(() => {
    localStorage.setItem("wwi_daily_focus", dailyFocus.toString());
  }, [dailyFocus]);
  
  // Quick add floating button state
  const [isQuickAddOpen, setIsQuickAddOpen] = useState(false);
  const [quickAddText, setQuickAddText] = useState("");
  const quickAddInputRef = useRef(null);
  
  // Board ref for pagination dots
  const boardRef = useRef(null);
  
  // App menu state
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [deferredInstallPrompt, setDeferredInstallPrompt] = useState(null);
  const [canInstall, setCanInstall] = useState(false);
  
  // Reminder opt-in state
  const [showReminderOptIn, setShowReminderOptIn] = useState(false);
  const reminderCheckDone = useRef(false);
  
  // Custom hooks for thread tracking and inactivity cues
  const {
    currentThreadId,
    updateCurrentThread,
    showOrientationStrip,
    showDimOverlay,
    orientationText,
    highlightedCardId,
    setHighlightedCardId,
    showThreadOrientation,
    dismissOrientation,
    markInitialStripShown,
    wasInitialStripShown,
  } = useCurrentThread();
  
  const { showPauseState, threadDriftGlow } = useInactivityCues(isDemo);
  
  // Action Guide visibility - always show for non-demo mode
  const showActionGuide = !isDemo;
  const [hasInteracted, setHasInteracted] = useState(false);
  
  // Track if user has seen/dismissed the "tap to edit" hint
  const [showEditHint, setShowEditHint] = useState(() => {
    const dismissed = localStorage.getItem('wwi_edit_hint_dismissed');
    const appOpens = parseInt(localStorage.getItem('wwi_app_opens') || '0', 10);
    // Show hint for first 3 app opens, unless dismissed
    return !dismissed && appOpens < 3;
  });
  
  // Track app opens
  useEffect(() => {
    const appOpens = parseInt(localStorage.getItem('wwi_app_opens') || '0', 10);
    localStorage.setItem('wwi_app_opens', String(appOpens + 1));
  }, []);
  
  // Dismiss edit hint permanently
  const dismissEditHint = useCallback(() => {
    setShowEditHint(false);
    localStorage.setItem('wwi_edit_hint_dismissed', 'true');
  }, []);

  // Auth context
  const { isAuthenticated, loading: authLoading, fetchUserData, saveUserData, user, signIn, signOut } = useAuth();
  
  // Nudge system for important tasks
  const nudgeSystem = useNudgeSystem(user?.id || null);
  const [showNudgePermissionModal, setShowNudgePermissionModal] = useState(false);
  const [pendingNudgeTask, setPendingNudgeTask] = useState(null); // {id, name} for pending nudge enable

  // Lock Screen focus system (experimental "Keep this on my Lock Screen")
  const lockScreen = useLockScreenFocus();
  const [showLockScreenModal, setShowLockScreenModal] = useState(false);
  const [lockScreenPendingTask, setLockScreenPendingTask] = useState(null); // {id, name}
  const handleLockScreenPin = useCallback((cardId, cardContent) => {
    setLockScreenPendingTask({ id: cardId, name: cardContent });
    setShowLockScreenModal(true);
  }, []);
  const handleLockScreenEnable = useCallback(async ({ intervalMinutes, pushSubscription }) => {
    if (!lockScreenPendingTask) return { ok: false, error: "No task selected" };
    // Capture "am I replacing an existing focus?" BEFORE we call enable(), since
    // enable() will overwrite the status. Different task id = replacement.
    const isReplacement = !!(
      lockScreen.status.enabled &&
      lockScreen.status.taskId &&
      lockScreen.status.taskId !== lockScreenPendingTask.id
    );
    const result = await lockScreen.enable({
      taskId: lockScreenPendingTask.id,
      taskName: lockScreenPendingTask.name,
      intervalMinutes,
      pushSubscription,
    });
    if (result.ok) {
      // Chime respects the global sound setting; two variants tell a story:
      //   fresh pin   → rising "yes" (E5→B5)
      //   replacement → soft "let go" (G5→F#5) then the same rising commit (B5)
      try {
        if (localStorage.getItem('wwi_sounds') !== 'false') {
          if (isReplacement) {
            playLockScreenReplaceSound();
          } else {
            playLockScreenPinSound();
          }
        }
      } catch (e) { /* ignore storage errors */ }
    }
    return result;
  }, [lockScreen, lockScreenPendingTask]);
  
  // Track if we just signed in (to handle data migration)
  const justSignedIn = location.state?.justSignedIn;
  const hasLoadedCloudData = useRef(false);
  const saveTimeoutRef = useRef(null);

  // Sound and settings hooks
  const { playCompletion, playProgress, playReset, playWaveSwoosh } = useSoundSettings();
  const { animationsEnabled, celebrationsEnabled, dailyReminderEnabled } = useAppSettings();
  const { smartWindowActive } = useMorningThread(dailyReminderEnabled);
  
  // Track when a task was just completed (for flame pulse animation)
  const [justCompletedTask, setJustCompletedTask] = useState(false);
  
  // Fire counter tracking
  const { 
    currentStreak, 
    longestStreak,
    totalCompleted,
    cycleCount,
    recordCompletion,
    decrementCount,
    newMilestone,
    dismissMilestone,
    setFireCounterFromCloud
  } = useStreak();

  // Track quarter mark milestone (25 tasks)
  const quarterMarkShownRef = useRef(false);
  // Track early momentum milestone (10 tasks)
  const earlyMomentumShownRef = useRef(false);
  // Track goal completion milestone (50 tasks)
  const goalCompletionShownRef = useRef(false);
  // Track century milestones (100, 200, 300... 900)
  const centuryMilestonesShownRef = useRef(new Set());
  // Track previous streak so Important tasks (weight 2) that skip exact values still fire milestones
  const prevStreakRef = useRef(currentStreak);
  
  useEffect(() => {
    const prev = prevStreakRef.current;
    const curr = currentStreak;
    // Detect forward crossing of a target (skips cycle rollovers like 999→1)
    const crossed = (target) => prev < target && curr >= target;
    
    // Early momentum at 10 tasks
    if (crossed(10) && !earlyMomentumShownRef.current) {
      earlyMomentumShownRef.current = true;
      setTimeout(() => {
        setShowEarlyMomentumModal(true);
      }, 1500);
    }
    // Quarter mark at 25 tasks
    if (crossed(25) && !quarterMarkShownRef.current) {
      quarterMarkShownRef.current = true;
      setTimeout(() => {
        setShowQuarterMarkModal(true);
      }, 1500);
    }
    // Goal completion at 50 tasks
    if (crossed(50) && !goalCompletionShownRef.current) {
      goalCompletionShownRef.current = true;
      setTimeout(() => {
        setShowGoalCompletionModal(true);
      }, 1500);
    }
    // Century milestones at 100, 200, 300... 900
    // Cycles 7 (700) and 8 (800) are intentionally silent
    const centuryMilestones = [100, 200, 300, 400, 500, 600, 900];
    for (const m of centuryMilestones) {
      if (crossed(m) && !centuryMilestonesShownRef.current.has(m)) {
        centuryMilestonesShownRef.current.add(m);
        setTimeout(() => {
          setShowCycleCompletionModal(true);
        }, 1500);
        break;
      }
    }
    
    prevStreakRef.current = curr;
  }, [currentStreak]);

  // Sensors for drag and drop (cards)
  const cardSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // Sensors for column dragging
  const columnSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 10 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // Auto-save to cloud (debounced)
  const saveToCloud = useCallback(async (boardData) => {
    if (!isAuthenticated || isDemo) return;
    
    setSyncStatus('syncing');
    try {
      const fireCounter = JSON.parse(localStorage.getItem('wwi_fire_counter') || '{}');
      const settings = {
        sounds: localStorage.getItem('wwi_sounds') !== 'false',
        animations: localStorage.getItem('wwi_animations') !== 'false',
        celebrations: localStorage.getItem('wwi_celebrations') !== 'false'
      };
      
      const success = await saveUserData(boardData, fireCounter, settings);
      setSyncStatus(success ? 'synced' : 'error');
      
      // Reset status after a delay
      setTimeout(() => setSyncStatus('idle'), 2000);
    } catch (error) {
      console.error('Cloud save error:', error);
      setSyncStatus('error');
    }
  }, [isAuthenticated, isDemo, saveUserData]);

  // Debounced save function
  const debouncedSaveToCloud = useCallback((boardData) => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    saveTimeoutRef.current = setTimeout(() => {
      saveToCloud(boardData);
    }, 2000); // Save 2 seconds after last change
  }, [saveToCloud]);

  // Load data on mount - auth-aware
  useEffect(() => {
    const loadData = async () => {
      console.log('[AppPage] loadData called - isDemo:', isDemo, 'authLoading:', authLoading, 'isAuthenticated:', isAuthenticated);
      
      // Demo mode - always use demo data
      if (isDemo) {
        setData(getDemoData());
        return;
      }

      // Track engagement for sync invitation
      trackEngagement();

      // Wait for auth to finish loading
      if (authLoading) {
        console.log('[AppPage] Waiting for auth to finish loading...');
        return;
      }

      // Get local data first
      let localData = null;
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        try {
          localData = JSON.parse(stored);
        } catch {
          localData = getEmptyData();
        }
      } else {
        localData = getEmptyData();
      }

      // If not authenticated, use local data only
      if (!isAuthenticated) {
        console.log('[AppPage] Not authenticated, using local data');
        setData(processListData(localData));
        hasLoadedCloudData.current = false;
        return;
      }

      // Authenticated user - fetch cloud data
      console.log('[AppPage] User is authenticated, fetching cloud data...');
      if (!hasLoadedCloudData.current) {
        setIsLoadingCloud(true);
        try {
          const cloudResponse = await fetchUserData();
          
          if (cloudResponse?.boards) {
            // User has cloud data - cloudResponse contains { boards, fire_counter, settings, ... }
            // boards is the full data structure: { boards: [...], currentBoardId: "..." }
            const cloudBoardData = cloudResponse.boards;
            const cloudFireCounter = cloudResponse.fire_counter;
            
            // Check if local data has content (non-empty boards)
            const localHasContent = localData?.boards?.[0]?.lists?.some(
              list => list.cards && list.cards.length > 0
            );
            
            // Check if cloud data has content
            const cloudHasContent = cloudBoardData?.boards?.[0]?.lists?.some(
              list => list.cards && list.cards.length > 0
            );
            
            // If user just signed in AND has local content AND cloud is empty
            if (justSignedIn && localHasContent && !cloudHasContent) {
              // Cloud is empty, migrate local data
              setData(processListData(localData));
              toast.success("Your local threads have been saved to your account!");
              debouncedSaveToCloud(processListData(localData));
            } else if (cloudHasContent) {
              // Cloud has data, use it (prioritize cloud for cross-device sync)
              const reorderedData = processListData(cloudBoardData);
              setData(reorderedData);
              localStorage.setItem(STORAGE_KEY, JSON.stringify(reorderedData));
              if (justSignedIn) {
                toast.success("Welcome back! Your threads are synced.");
              }
            } else {
              // Both empty or cloud empty - use local
              setData(processListData(localData));
            }
            
            // Restore fire counter from cloud
            if (cloudFireCounter) {
              localStorage.setItem('wwi_fire_counter', JSON.stringify(cloudFireCounter));
              if (setFireCounterFromCloud) {
                setFireCounterFromCloud(cloudFireCounter);
              }
            }
          } else {
            // No cloud data - use local and save to cloud if user has content
            setData(processListData(localData));
            const localHasContent = localData?.boards?.[0]?.lists?.some(
              list => list.cards && list.cards.length > 0
            );
            if (localHasContent) {
              debouncedSaveToCloud(processListData(localData));
              toast.success("Your threads are now synced to your account!");
            }
          }
          
          hasLoadedCloudData.current = true;
        } catch (error) {
          console.error('Error loading cloud data:', error);
          setData(localData);
        } finally {
          setIsLoadingCloud(false);
        }
      }
    };

    loadData();
  }, [isDemo, authLoading, isAuthenticated, fetchUserData, justSignedIn, debouncedSaveToCloud, setFireCounterFromCloud]);

  // Handle ?focus={taskId} parameter from /now page
  // Auto-scrolls to the task and highlights it
  useEffect(() => {
    if (!focusTaskId || !data?.boards?.[0]) return;
    
    const board = data.boards[0];
    
    // Find the task to focus on
    let foundCard = null;
    
    for (const list of board.lists) {
      const card = list.cards.find(c => c.id === focusTaskId);
      if (card) {
        foundCard = card;
        break;
      }
    }
    
    if (foundCard) {
      // Small delay to ensure DOM is rendered
      setTimeout(() => {
        // Update current thread
        updateCurrentThread(focusTaskId);
        
        // Show orientation and scroll to card
        showThreadOrientation(foundCard.content, focusTaskId, true);
      }, 400);
    }
  }, [focusTaskId, data, updateCurrentThread, showThreadOrientation]);

  // Save to localStorage AND cloud whenever data changes
  useEffect(() => {
    if (data && !isDemo && !isLoadingCloud) {
      // Always save to localStorage
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
      
      // Auto-save to cloud if authenticated
      if (isAuthenticated && hasLoadedCloudData.current) {
        debouncedSaveToCloud(data);
      }
    }
  }, [data, isDemo, isAuthenticated, debouncedSaveToCloud, isLoadingCloud]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  // Capture beforeinstallprompt for the header menu install option
  const menuRef = useRef(null);
  useEffect(() => {
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredInstallPrompt(e);
      setCanInstall(true);
    };
    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    // Close menu when clicking outside
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setIsMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleMenuInstall = async () => {
    if (deferredInstallPrompt) {
      deferredInstallPrompt.prompt();
      const { outcome } = await deferredInstallPrompt.userChoice;
      if (outcome === 'accepted') {
        setCanInstall(false);
      }
      setDeferredInstallPrompt(null);
    }
    setIsMenuOpen(false);
  };

  // Check if we should show the reminder opt-in prompt
  // Shows after user has completed 3+ tasks (checked on each completion)
  useEffect(() => {
    if (reminderCheckDone.current || isDemo) return;
    
    // Check after a short delay to let the UI settle
    const checkTimer = setTimeout(() => {
      if (shouldShowReminderOptIn()) {
        setShowReminderOptIn(true);
        reminderCheckDone.current = true;
      }
    }, 2000);
    
    return () => clearTimeout(checkTimer);
  }, [sessionCompletedCount, isDemo]);

  // Handle reminder opt-in close
  const handleReminderOptInClose = useCallback((result) => {
    setShowReminderOptIn(false);
    if (result === 'push' || result === 'email') {
      // User opted in - update activity on backend if authenticated
      if (isAuthenticated && user?.user_id) {
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/reminders/activity`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            userId: user.user_id,
            completedCount: parseInt(localStorage.getItem('wwi_completed_count') || '0', 10)
          })
        }).catch(console.error);
      }
    }
  }, [isAuthenticated, user]);

  // Track user interaction for action guide collapse
  const handleUserInteraction = useCallback(() => {
    setHasInteracted(true);
  }, []);

  // Wrap updateCurrentThread to also track interaction
  const trackAndUpdateThread = useCallback((cardId) => {
    updateCurrentThread(cardId);
    handleUserInteraction();
  }, [updateCurrentThread, handleUserInteraction]);

  // Keyboard shortcut for search (Cmd/Ctrl + K)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsSearchOpen(true);
        setTimeout(() => searchInputRef.current?.focus(), 100);
      }
      if (e.key === 'Escape' && isSearchOpen) {
        setSearchQuery("");
        setIsSearchOpen(false);
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isSearchOpen]);

  // Get current board
  const currentBoard = data?.boards?.[0];

  // --- Current Thread: resolve, show orientation, find ---
  const resolveThread = useCallback((threadId) => {
    if (!threadId || !currentBoard) return null;
    for (const list of currentBoard.lists) {
      const card = list.cards.find(c => c.id === threadId);
      if (card) return { card, listId: list.id, listName: list.name };
    }
    return null;
  }, [currentBoard]);

  const showThreadOrientationLocal = useCallback((threadId) => {
    const info = resolveThread(threadId || currentThreadId);
    if (!info) {
      showThreadOrientation(null, null, false);
      return;
    }
    showThreadOrientation(info.card.content, info.card.id, true);
  }, [resolveThread, currentThreadId, showThreadOrientation]);

  useEffect(() => {
    if (data && currentBoard && currentThreadId && !wasInitialStripShown() && !isDemo) {
      markInitialStripShown();
      setTimeout(() => showThreadOrientationLocal(currentThreadId), 600);
    }
  }, [data, currentBoard, currentThreadId, isDemo, showThreadOrientationLocal, wasInitialStripShown, markInitialStripShown]);

  const handleFindMyThread = () => {
    // Get the task name for the ritual display
    let taskName = null;
    if (currentThreadId) {
      const list = findListByCardId(currentThreadId);
      if (list) {
        const card = list.cards.find(c => c.id === currentThreadId);
        if (card) {
          taskName = card.content;
        }
      }
    }
    
    // Set ritual task name and show ritual
    setRitualTaskName(taskName);
    setShowFindThreadRitual(true);
  };
  
  // Called when ritual completes
  const handleRitualComplete = () => {
    setShowFindThreadRitual(false);
    
    // Now perform the actual thread finding
    if (currentThreadId && resolveThread(currentThreadId)) {
      showThreadOrientationLocal(currentThreadId);
    } else {
      showThreadOrientationLocal(null);
      setShowResetModal(true);
    }
  };

  // Find which list contains a card
  const findListByCardId = (cardId) => {
    if (!currentBoard) return null;
    for (const list of currentBoard.lists) {
      if (list.cards.some(c => c.id === cardId)) {
        return list;
      }
    }
    return null;
  };

  // Get all critical tasks with their list context
  const getCriticalTasks = () => {
    if (!currentBoard) return [];
    const criticalTasks = [];
    
    for (const list of currentBoard.lists) {
      const isDoneList = list.name.toLowerCase() === "done";
      for (const card of list.cards) {
        if (card.isCritical) {
          criticalTasks.push({
            ...card,
            listName: list.name,
            isDone: isDoneList
          });
        }
      }
    }
    
    // Sort by most recent first (non-done tasks prioritized)
    return criticalTasks.sort((a, b) => {
      // Non-done tasks come first
      if (a.isDone !== b.isDone) return a.isDone ? 1 : -1;
      // Then by creation date (most recent first)
      return new Date(b.createdAt) - new Date(a.createdAt);
    });
  };

  // Drag handlers for cards
  const handleDragStart = (event) => {
    const { active } = event;
    const list = findListByCardId(active.id);
    if (list) {
      const card = list.cards.find(c => c.id === active.id);
      setActiveCard(card);
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    
    // Card drag handling only
    setActiveCard(null);

    if (!over || !currentBoard) return;

    const activeList = findListByCardId(active.id);
    if (!activeList) return;

    const activeCardIndex = activeList.cards.findIndex(c => c.id === active.id);
    const activeCard = activeList.cards[activeCardIndex];

    // Check if dropping on a card or a list
    let overList = findListByCardId(over.id);
    let overCardIndex = -1;

    if (overList) {
      overCardIndex = overList.cards.findIndex(c => c.id === over.id);
    } else {
      // Might be dropping on a list directly
      overList = currentBoard.lists.find(l => l.id === over.id);
    }

    if (!overList) return;

    // Same list reorder
    if (activeList.id === overList.id) {
      if (overCardIndex !== -1 && activeCardIndex !== overCardIndex) {
        const isDoingColumn = activeList.name.toLowerCase() === "doing" || activeList.name.toLowerCase() === "in progress";
        
        setData(prev => {
          const newData = JSON.parse(JSON.stringify(prev));
          const board = newData.boards[0];
          const list = board.lists.find(l => l.id === activeList.id);
          
          // For the Doing column with grouped subsections, infer category from neighbors after move
          if (isDoingColumn) {
            // Sort cards by category first (to match visual order)
            const hasComms = list.cards.some(c => c.category === 'email' || c.category === 'phone');
            if (hasComms) {
              const catOrder = { action: 0, email: 1, phone: 2 };
              const sorted = [...list.cards].sort((a, b) => (catOrder[a.category || 'action'] || 0) - (catOrder[b.category || 'action'] || 0));
              const sortedActiveIdx = sorted.findIndex(c => c.id === active.id);
              const sortedOverIdx = sorted.findIndex(c => c.id === over.id);
              const reordered = arrayMove(sorted, sortedActiveIdx, sortedOverIdx);
              // Infer category from the card above the new position
              const movedIdx = sortedOverIdx;
              const above = movedIdx > 0 ? reordered[movedIdx - 1] : null;
              const below = movedIdx < reordered.length - 1 ? reordered[movedIdx + 1] : null;
              const movedCard = reordered[movedIdx];
              if (above) movedCard.category = above.category || 'action';
              else if (below) movedCard.category = below.category || 'action';
              list.cards = reordered;
            } else {
              list.cards = arrayMove(list.cards, activeCardIndex, overCardIndex);
            }
          } else {
            list.cards = arrayMove(list.cards, activeCardIndex, overCardIndex);
          }
          return newData;
        });
      }
    } else {
      // Move to different list
      const isDoneList = overList.name.toLowerCase() === "done";
      const isFromDone = activeList.name.toLowerCase() === "done";
      const isInProgressList = overList.name.toLowerCase() === "doing" || overList.name.toLowerCase() === "in progress";
      const isFromToDo = activeList.name.toLowerCase() === "to do";
      
      setData(prev => {
        const newData = JSON.parse(JSON.stringify(prev));
        const board = newData.boards[0];
        const fromList = board.lists.find(l => l.id === activeList.id);
        const toList = board.lists.find(l => l.id === overList.id);
        
        // Remove from source
        fromList.cards = fromList.cards.filter(c => c.id !== activeCard.id);
        
        // Add to destination — stamp completedAt if moving to Done
        const cardToAdd = isDoneList ? { ...activeCard, completedAt: new Date().toISOString() } : { ...activeCard };
        // Clear completedAt if moving out of Done
        if (isFromDone && !isDoneList) delete cardToAdd.completedAt;
        // Auto-detect category when entering Doing column
        if (isInProgressList) {
          const detected = detectCategory(cardToAdd.content);
          if (detected !== 'action') {
            cardToAdd.category = detected;
          }
        }
        
        if (overCardIndex !== -1) {
          toList.cards.splice(overCardIndex, 0, cardToAdd);
        } else {
          toList.cards.push(cardToAdd);
        }
        
        return newData;
      });

      // Handle fire counter and sounds based on movement
      if (isDoneList && celebrationsEnabled) {
        // Track session completion count first to determine milestone
        const newCount = sessionCompletedCount + 1;
        setSessionCompletedCount(newCount);
        
        // Check if this is a milestone completion (3, 9, 18)
        const isMilestone = newCount === 3 || newCount === 9 || newCount === 18;
        
        // Calculate upcoming streak count for special milestone sound
        const upcomingStreak = currentStreak + 1;
        
        // Completion sequence: thump → ripple → flame fed (synced with end click)
        // 1. Haptic double-thump immediately (0ms) - feel it first
        if (isMilestone) {
          triggerMilestoneHaptic(upcomingStreak);
        } else {
          triggerHeartbeatHaptic(false, upcomingStreak);
        }
        
        // 2. Ripple + particles start (50ms) - see the ripple
        setTimeout(() => {
          setShowCelebration(true);
        }, 50);
        
        // 3. Counter rolls just before the click sound at end of animation
        setTimeout(() => {
          setJustCompletedTask(true);
          recordCompletion(activeCard.isCritical ? 2 : 1);
          setTimeout(() => setJustCompletedTask(false), 800);
        }, 2700);

        // Cancel lock-screen focus if this task was pinned
        lockScreen.notifyTaskCompleted(activeCard.id, activeCard.content);

        // Trigger install prompt on first task completion
        if (newCount === 1) {
          triggerInstallPrompt(INSTALL_PROMPT_EVENTS.TASK_COMPLETED);
        }
        
        // Mark milestone as hit if reached
        if (newCount >= 18 && !sessionMilestonesHit.includes(18)) {
          setSessionMilestonesHit(prev => [...prev, 18]);
        } else if (newCount >= 9 && !sessionMilestonesHit.includes(9)) {
          setSessionMilestonesHit(prev => [...prev, 9]);
        } else if (newCount >= 3 && !sessionMilestonesHit.includes(3)) {
          setSessionMilestonesHit(prev => [...prev, 3]);
        }
      } else if (isDoneList && !celebrationsEnabled) {
        // Track session completion count first
        const newCount = sessionCompletedCount + 1;
        setSessionCompletedCount(newCount);
        
        // Check if milestone
        const isMilestone = newCount === 3 || newCount === 9 || newCount === 18;
        
        // Calculate upcoming streak count for special milestone sound
        const upcomingStreak = currentStreak + 1;
        
        // Haptic double-thump first
        if (isMilestone) {
          triggerMilestoneHaptic(upcomingStreak);
        } else {
          triggerHeartbeatHaptic(false, upcomingStreak);
        }
        
        // Counter rolls just before the click sound at end of animation
        setTimeout(() => {
          setJustCompletedTask(true);
          recordCompletion(activeCard.isCritical ? 2 : 1);
          setTimeout(() => setJustCompletedTask(false), 800);
        }, 2700);

        // Cancel lock-screen focus if this task was pinned
        lockScreen.notifyTaskCompleted(activeCard.id, activeCard.content);

        // Trigger install prompt on first task completion
        if (newCount === 1) {
          triggerInstallPrompt(INSTALL_PROMPT_EVENTS.TASK_COMPLETED);
        }
        
        // Mark milestone as hit
        if (newCount >= 18 && !sessionMilestonesHit.includes(18)) {
          setSessionMilestonesHit(prev => [...prev, 18]);
        } else if (newCount >= 9 && !sessionMilestonesHit.includes(9)) {
          setSessionMilestonesHit(prev => [...prev, 9]);
        } else if (newCount >= 3 && !sessionMilestonesHit.includes(3)) {
          setSessionMilestonesHit(prev => [...prev, 3]);
        }
      } else if (isFromDone) {
        // Moving FROM Done - decrement counter (undoing completion)
        decrementCount(activeCard.isCritical ? 2 : 1);
        if (isInProgressList) {
          playWaveSwoosh();
        } else {
          playProgress();
        }
      } else if (isInProgressList && isFromToDo) {
        // Ocean wave swoosh when moving from To Do to Doing
        playWaveSwoosh();
      } else {
        playProgress();
      }
      
      // Cancel any pending nudge when task is moved to Done
      if (isDoneList && activeCard.nudgeEnabled) {
        nudgeSystem?.cancelNudge(activeCard.id);
      }
    }
    
    // Track dragged card as current thread
    trackAndUpdateThread(active.id);
    
    // Record activity for nudge system
    nudgeSystem?.recordActivity('move');
  };
  const handleColumnDragStart = (event) => {
    const { active } = event;
    const column = currentBoard?.lists.find(l => l.id === active.id);
    if (column) {
      setActiveColumn(column);
    }
  };

  const handleColumnDragEnd = (event) => {
    const { active, over } = event;
    setActiveColumn(null);

    if (!over || !currentBoard || active.id === over.id) return;

    const oldIndex = currentBoard.lists.findIndex(l => l.id === active.id);
    const newIndex = currentBoard.lists.findIndex(l => l.id === over.id);

    if (oldIndex !== -1 && newIndex !== -1) {
      setData(prev => {
        const newData = JSON.parse(JSON.stringify(prev));
        newData.boards[0].lists = arrayMove(newData.boards[0].lists, oldIndex, newIndex);
        
        // Save column order to localStorage
        const columnOrder = newData.boards[0].lists.map(l => l.id);
        localStorage.setItem('wwi_column_order', JSON.stringify(columnOrder));
        
        return newData;
      });
    }
  };

  // Card operations
  const handleAddCard = (listId, content, isImportant = false) => {
    const newCard = {
      id: uuidv4(),
      content,
      notes: "",
      createdAt: new Date().toISOString(),
      isCritical: isImportant,
      category: detectCategory(content)
    };
    
    setData(prev => {
      const newData = JSON.parse(JSON.stringify(prev));
      const list = newData.boards[0].lists.find(l => l.id === listId);
      if (list) {
        list.cards.push(newCard);
      }
      return newData;
    });
    
    // Track engagement for sync invitation
    if (!isDemo) {
      incrementTaskCount();
    }
    
    // Trigger first task onboarding event
    triggerFirstTaskAdded();
    
    // Track as current thread
    trackAndUpdateThread(newCard.id);
  };

  // Quick add card to To Do list
  const handleQuickAdd = () => {
    if (!quickAddText.trim() || !currentBoard) return;
    
    // Find the "To Do" list
    const toDoList = currentBoard.lists.find(l => l.name.toLowerCase() === 'to do');
    if (toDoList) {
      handleAddCard(toDoList.id, quickAddText.trim());
      triggerHaptic('light');
    }
    
    setQuickAddText("");
    setIsQuickAddOpen(false);
    // Reset iOS viewport zoom
    if (quickAddInputRef.current) quickAddInputRef.current.blur();
    window.scrollTo(0, 0);
  };

  const handleEditCard = (card) => {
    setEditingCard(card);
  };

  const handleSaveCard = async (updatedCard) => {
    const oldCard = editingCard;
    
    setData(prev => {
      const newData = JSON.parse(JSON.stringify(prev));
      for (const list of newData.boards[0].lists) {
        const cardIndex = list.cards.findIndex(c => c.id === updatedCard.id);
        if (cardIndex !== -1) {
          list.cards[cardIndex] = updatedCard;
          break;
        }
      }
      return newData;
    });
    setEditingCard(null);
    
    // Track as current thread
    trackAndUpdateThread(updatedCard.id);
    
    // Handle nudge changes — Important and Nudge are independent toggles now,
    // so we enable/cancel based purely on the nudge flag transition. Toggling
    // Important on its own no longer schedules or cancels a nudge. Backend
    // accepts a null push subscription and falls back to email delivery, so
    // this path works on iOS Safari too.
    if (user?.id && nudgeSystem) {
      const oldNudgeEnabled = oldCard?.nudgeEnabled || false;
      const newNudgeEnabled = updatedCard.nudgeEnabled || false;

      if (newNudgeEnabled && !oldNudgeEnabled) {
        const result = await nudgeSystem.enableNudge(updatedCard.id, updatedCard.content);
        if (result && result.success === false && result.error === 'free_tier_cap_reached') {
          // Roll the card's nudge flag back so UI matches server state
          toast.error(result.message || `Free plan limit: ${result.limit} active nudges. Finish one first.`, {
            duration: 4500,
          });
          setData(prev => {
            const newData = JSON.parse(JSON.stringify(prev));
            for (const list of newData.boards[0].lists) {
              const c = list.cards.find(x => x.id === updatedCard.id);
              if (c) { c.nudgeEnabled = false; break; }
            }
            return newData;
          });
        } else {
          nudgeSystem.recordActivity?.('edit');
        }
      } else if (!newNudgeEnabled && oldNudgeEnabled) {
        await nudgeSystem.cancelNudge(updatedCard.id);
      }
    }
  };

  const handleDeleteCard = (cardId, listId) => {
    // Read the card BEFORE the state update so we can fire side effects (like
    // cancelling a nudge) exactly once — React 18 StrictMode runs updater
    // functions twice in dev, which would double-fire the API call.
    const list = currentBoard?.lists.find(l => l.id === listId);
    const deletedCard = list?.cards.find(c => c.id === cardId);

    setData(prev => {
      const newData = JSON.parse(JSON.stringify(prev));
      const l = newData.boards[0].lists.find(x => x.id === listId);
      if (l) {
        l.cards = l.cards.filter(c => c.id !== cardId);
      }
      return newData;
    });

    // Cancel any pending nudge for the deleted task (side effect outside updater)
    if (deletedCard?.isCritical && deletedCard?.nudgeEnabled) {
      nudgeSystem?.cancelNudge(cardId);
    }
  };

  // Toggle critical (Important) status for a card. Nudge state is independent
  // and managed via handleToggleNudge — marking Important no longer auto-turns
  // on the email nudge.
  const handleToggleCritical = async (cardId) => {
    let willBeImportant = false;
    for (const list of currentBoard?.lists || []) {
      const card = list.cards.find(c => c.id === cardId);
      if (card) {
        willBeImportant = !card.isCritical;
        break;
      }
    }

    setData(prev => {
      const newData = JSON.parse(JSON.stringify(prev));
      for (const list of newData.boards[0].lists) {
        const card = list.cards.find(c => c.id === cardId);
        if (card) {
          card.isCritical = willBeImportant;
          break;
        }
      }
      return newData;
    });
  };
  
  // Handle nudge permission request from CardEditModal
  const handleShowNudgePermission = (taskId, taskName) => {
    setPendingNudgeTask({ id: taskId, name: taskName });
    setShowNudgePermissionModal(true);
  };
  
  // Handle nudge permission granted
  const handleNudgePermissionGranted = async () => {
    setShowNudgePermissionModal(false);
    
    if (pendingNudgeTask && user?.id) {
      // Enable nudge for the pending task
      const result = await nudgeSystem.enableNudge(pendingNudgeTask.id, pendingNudgeTask.name);
      if (result && result.success === false && result.error === 'free_tier_cap_reached') {
        toast.error(result.message || `Free plan limit: ${result.limit} active nudges.`, { duration: 4500 });
        setPendingNudgeTask(null);
        return;
      }

      // Update the card's nudgeEnabled state
      setData(prev => {
        const newData = JSON.parse(JSON.stringify(prev));
        for (const list of newData.boards[0].lists) {
          const card = list.cards.find(c => c.id === pendingNudgeTask.id);
          if (card) {
            card.nudgeEnabled = true;
            break;
          }
        }
        return newData;
      });
      
      // Also update the editingCard if it's the same
      if (editingCard?.id === pendingNudgeTask.id) {
        setEditingCard(prev => prev ? { ...prev, nudgeEnabled: true } : null);
      }
      
      toast.success('Email nudge set for 24 hours');
    }
    
    setPendingNudgeTask(null);
  };
  
  // Handle nudge toggle from card bell icon
  const handleToggleNudge = async (cardId, cardContent, enable) => {
    // Feature flag: while email delivery is being finalized, tapping the
    // bell shows a friendly "coming soon" toast instead of scheduling a
    // nudge we can't reliably deliver yet. Remove this block once the
    // Resend sender domain is verified.
    if (!EMAIL_NUDGES_ENABLED) {
      toast("Email nudges are coming soon — we'll switch this on once delivery is dialed in.", {
        duration: 3500,
        icon: '✉️',
      });
      return;
    }

    // Disable path always works — cancel any scheduled nudge + clear the card flag.
    if (!enable) {
      await nudgeSystem?.cancelNudge?.(cardId);
      setData(prev => {
        const newData = JSON.parse(JSON.stringify(prev));
        for (const list of newData.boards[0].lists) {
          const card = list.cards.find(c => c.id === cardId);
          if (card) {
            card.nudgeEnabled = false;
            break;
          }
        }
        return newData;
      });
      toast.success("Nudge disabled", { duration: 2000, icon: '🔕' });
      return;
    }

    // Enabling path — need a signed-in user so the backend can route the
    // email fallback (nudges are ALWAYS emailed when we have a user email,
    // push is a nice-to-have on top).
    if (!user) {
      toast("Sign in to enable nudges.", { duration: 3500, icon: '✉️' });
      return;
    }

    const pushSupported = !!nudgeSystem?.isSupported;
    const pushGranted = !!nudgeSystem?.hasPermission;
    const pushDenied = !!nudgeSystem?.permissionDenied;

    // Push is available and we haven't asked yet → open the permission modal
    // so the user gets a native prompt at the moment they showed intent.
    if (pushSupported && !pushGranted && !pushDenied) {
      setPendingNudgeTask({ id: cardId, name: cardContent });
      setShowNudgePermissionModal(true);
      return;
    }

    // All other cases (push granted, push denied, or push unsupported like
    // iOS Safari in browser mode) — enable the nudge now. If push isn't
    // available we still schedule an email nudge as the reliable fallback,
    // so the bell reflects a real, working state.
    const result = await nudgeSystem.enableNudge(cardId, cardContent);
    if (result && result.success === false && result.error === 'free_tier_cap_reached') {
      toast.error(result.message || `Free plan limit: ${result.limit} active nudges. Finish one first.`, {
        duration: 4500,
      });
      return;
    }
    nudgeSystem.recordActivity?.('toggle_nudge');

    setData(prev => {
      const newData = JSON.parse(JSON.stringify(prev));
      for (const list of newData.boards[0].lists) {
        const card = list.cards.find(c => c.id === cardId);
        if (card) {
          card.nudgeEnabled = true;
          break;
        }
      }
      return newData;
    });

    // Copy tuned to what the user will actually experience: push if the
    // browser/PWA can deliver it, otherwise an email nudge in 24 hrs.
    if (pushGranted) {
      toast.success("Email + lock-screen nudge set", { duration: 2400, icon: '🔔' });
    } else {
      toast.success("Email nudge set — we'll ping you in 24 hrs if the task is still open", {
        duration: 3500,
        icon: '✉️',
      });
    }
  };

  // Share a task - open share modal
  const handleShareTask = (card) => {
    if (!user) {
      toast.error('Sign in to share tasks');
      return;
    }
    setShareTask(card);
    setShowShareModal(true);
  };

  // Share this app
  const handleShareApp = async () => {
    const shareData = {
      title: 'Where Was I',
      text: 'A calm, focused task app for ADHD minds. Try it free.',
      url: 'https://getwherewasi.com'
    };
    
    try {
      if (navigator.share) {
        // Mobile: use native share
        await navigator.share(shareData);
      } else {
        // Desktop: show share modal with options
        setShowShareAppModal(true);
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        // User didn't cancel, show modal as fallback
        setShowShareAppModal(true);
      }
    }
  };
  
  // Copy app link to clipboard
  const copyAppLink = async () => {
    try {
      await navigator.clipboard.writeText('https://getwherewasi.com');
      toast.success('Link copied!');
    } catch {
      toast.error('Could not copy');
    }
  };
  
  // Share via email
  const shareViaEmail = () => {
    const subject = encodeURIComponent('Check out Where Was I');
    const body = encodeURIComponent('A calm, focused task app for ADHD minds. Try it free.\n\nhttps://getwherewasi.com');
    window.open(`mailto:?subject=${subject}&body=${body}`, '_blank');
  };
  
  // Share via Twitter/X
  const shareViaTwitter = () => {
    const text = encodeURIComponent('A calm, focused task app for ADHD minds. Try it free.');
    const url = encodeURIComponent('https://getwherewasi.com');
    window.open(`https://twitter.com/intent/tweet?text=${text}&url=${url}`, '_blank', 'width=550,height=420');
  };
  
  // Share via LinkedIn
  const shareViaLinkedIn = () => {
    const url = encodeURIComponent('https://getwherewasi.com');
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${url}`, '_blank', 'width=550,height=420');
  };
  
  // Share via Facebook
  const shareViaFacebook = () => {
    const url = encodeURIComponent('https://getwherewasi.com');
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}`, '_blank', 'width=550,height=420');
  };
  
  // Dismiss share app button
  const dismissShareAppButton = () => {
    setShowShareAppButton(false);
    localStorage.setItem('wwi_share_app_dismissed', 'true');
  };

  // Mark card as done - move to Done list
  const handleMarkDone = (cardId, fromListId, cardElement) => {
    if (!currentBoard) return;
    
    // Capture card position for celebration origin
    if (cardElement) {
      const rect = cardElement.getBoundingClientRect();
      setCelebrationOrigin({
        x: rect.left + rect.width / 2,
        y: rect.top + rect.height / 2,
      });
    }
    
    // Find the Done list
    const doneList = currentBoard.lists.find(l => l.name.toLowerCase() === "done");
    if (!doneList) return;
    
    // Find the card
    const fromList = currentBoard.lists.find(l => l.id === fromListId);
    if (!fromList) return;
    
    const card = fromList.cards.find(c => c.id === cardId);
    if (!card) return;
    
    // Move card to Done list
    setData(prev => {
      const newData = JSON.parse(JSON.stringify(prev));
      const board = newData.boards[0];
      const sourceList = board.lists.find(l => l.id === fromListId);
      const targetList = board.lists.find(l => l.name.toLowerCase() === "done");
      
      if (sourceList && targetList) {
        // Remove from source
        sourceList.cards = sourceList.cards.filter(c => c.id !== cardId);
        // Add to Done with completion timestamp
        const completedCard = { ...card, completedAt: new Date().toISOString() };
        targetList.cards.push(completedCard);
      }
      
      return newData;
    });
    
    // Track session completion for acknowledgment
    const newCount = sessionCompletedCount + 1;
    setSessionCompletedCount(newCount);
    
    // Check if this is a milestone completion (3, 9, 18)
    const isMilestone = newCount === 3 || newCount === 9 || newCount === 18;
    
    // Calculate upcoming streak count for special milestone sound
    const upcomingStreak = currentStreak + 1;
    
    // Check if this completion triggers a major milestone modal (10, 25, 50, 100)
    // These milestones have their own celebrations, so skip the regular ripple
    const isMajorMilestone = upcomingStreak === 10 || upcomingStreak === 25 || upcomingStreak === 50 || upcomingStreak === 100;
    
    // Mark milestone as hit if reached
    if (newCount >= 18 && !sessionMilestonesHit.includes(18)) {
      setSessionMilestonesHit(prev => [...prev, 18]);
    } else if (newCount >= 9 && !sessionMilestonesHit.includes(9)) {
      setSessionMilestonesHit(prev => [...prev, 9]);
    } else if (newCount >= 3 && !sessionMilestonesHit.includes(3)) {
      setSessionMilestonesHit(prev => [...prev, 3]);
    }
    
    // Completion sequence: thump → ripple → flame fed (synced with end click)
    // 1. Haptic double-thump immediately (0ms) - skip for major milestones (they have their own haptics)
    if (!isMajorMilestone) {
      if (isMilestone) {
        triggerMilestoneHaptic(upcomingStreak);
      } else {
        triggerHeartbeatHaptic(false, upcomingStreak);
      }
    }
    
    // 2. Ripple + particles start (50ms) - skip for major milestones (they have their own celebrations)
    if (celebrationsEnabled && !isMajorMilestone) {
      setTimeout(() => {
        setShowCelebration(true);
      }, 50);
    }
    
    // 3. Counter rolls just before the click sound at end of animation
    setTimeout(() => {
      setJustCompletedTask(true);
      recordCompletion(card.isCritical ? 2 : 1);
      setTimeout(() => setJustCompletedTask(false), 800);
    }, 2700);

    // Cancel lock-screen focus if this task was pinned
    lockScreen.notifyTaskCompleted(card.id, card.content);

    // Cancel any pending nudge for this completed task so the user doesn't
    // receive a "gentle reminder" email for something they already finished.
    if (card.isCritical && card.nudgeEnabled) {
      nudgeSystem?.cancelNudge(card.id);
    }

    // Track task completion for install prompt (new dual-condition trigger)
    trackTaskCompletion();
  };

  // Move card to a specific list (for undoing done items)
  const handleMoveToList = (cardId, fromListId, targetListName) => {
    if (!currentBoard) return;
    
    // Find the target list by name
    const targetList = currentBoard.lists.find(l => l.name.toLowerCase() === targetListName.toLowerCase());
    if (!targetList) return;
    
    // Find the source list and card
    const fromList = currentBoard.lists.find(l => l.id === fromListId);
    if (!fromList) return;
    
    const card = fromList.cards.find(c => c.id === cardId);
    if (!card) return;
    
    // Check if moving from Done list (to decrement fire counter)
    const isFromDone = fromList.name.toLowerCase() === "done";
    
    // Move card to target list
    setData(prev => {
      const newData = JSON.parse(JSON.stringify(prev));
      const board = newData.boards[0];
      const sourceList = board.lists.find(l => l.id === fromListId);
      const destList = board.lists.find(l => l.name.toLowerCase() === targetListName.toLowerCase());
      
      if (sourceList && destList) {
        // Remove from source
        sourceList.cards = sourceList.cards.filter(c => c.id !== cardId);
        const isDoneTarget = targetListName.toLowerCase() === "done";
        // Add completedAt when moving to Done, clear it otherwise
        const cardToAdd = { ...card };
        if (isDoneTarget) {
          cardToAdd.completedAt = new Date().toISOString();
        } else {
          delete cardToAdd.completedAt;
          cardToAdd.createdAt = new Date().toISOString();
        }
        // Auto-detect category when entering Doing column
        const isMovingToDoing = targetListName.toLowerCase() === "doing" || targetListName.toLowerCase() === "in progress";
        if (isMovingToDoing) {
          const detected = detectCategory(card.content);
          if (detected !== 'action') {
            cardToAdd.category = detected;
          }
        }
        // Add to target (at the beginning so it's visible)
        destList.cards.unshift(cardToAdd);
      }
      
      return newData;
    });
    
    // Decrement fire counter when undoing a completion
    if (isFromDone) {
      decrementCount(card.isCritical ? 2 : 1);
    }

    // Cancel any pending nudge if moving to Done (mirror handleMarkDone behaviour)
    const isDoneTarget = targetListName.toLowerCase() === "done";
    if (isDoneTarget && card.isCritical && card.nudgeEnabled) {
      nudgeSystem?.cancelNudge(cardId);
    }

    // Play appropriate sound
    if (targetListName.toLowerCase() === "doing" || targetListName.toLowerCase() === "in progress") {
      playWaveSwoosh();
      // Track as current thread when moving to Doing
      trackAndUpdateThread(cardId);
      // Save last active task for /now page
      const cardWithCategory = { ...card, category: card.category || detectCategory(card.content) };
      saveLastActiveTask(cardWithCategory, targetListName);
    } else {
      playProgress();
    }
  };

  // List operations
  const handleAddList = () => {
    if (newListName.trim()) {
      const newList = {
        id: uuidv4(),
        name: newListName.trim(),
        cards: []
      };
      
      setData(prev => {
        const newData = JSON.parse(JSON.stringify(prev));
        newData.boards[0].lists.push(newList);
        return newData;
      });
      
      setNewListName("");
      setIsAddingList(false);
    }
  };

  const handleRenameList = (listId, newName) => {
    setData(prev => {
      const newData = JSON.parse(JSON.stringify(prev));
      const list = newData.boards[0].lists.find(l => l.id === listId);
      if (list) {
        list.name = newName;
      }
      return newData;
    });
  };

  const handleDeleteList = (listId) => {
    setData(prev => {
      const newData = JSON.parse(JSON.stringify(prev));
      newData.boards[0].lists = newData.boards[0].lists.filter(l => l.id !== listId);
      return newData;
    });
  };

  // Reset ritual completion - search for matching cards
  const handleResetComplete = (thought) => {
    if (!currentBoard) {
      setShowResetModal(false);
      return;
    }
    
    const searchText = thought.toLowerCase().trim();
    const searchWords = searchText.split(/\s+/).filter(w => w.length > 2); // Words with 3+ chars
    
    // Search in To Do and Doing lists (not Done)
    const searchableLists = currentBoard.lists.filter(
      l => l.name.toLowerCase() !== "done"
    );
    
    // Find all matching cards with their list info
    const matches = [];
    searchableLists.forEach(list => {
      list.cards.forEach(card => {
        const cardText = (card.content + " " + (card.notes || "")).toLowerCase();
        // Check if any search word matches
        const hasMatch = searchWords.some(word => cardText.includes(word));
        if (hasMatch) {
          matches.push({
            card,
            listId: list.id,
            listName: list.name,
            createdAt: new Date(card.createdAt || 0)
          });
        }
      });
    });
    
    if (matches.length > 0) {
      // Sort by most recent first
      matches.sort((a, b) => b.createdAt - a.createdAt);
      const bestMatch = matches[0];
      
      // Highlight the card and scroll to it
      setHighlightedCardId(bestMatch.card.id);
      
      // Scroll to the card after a brief delay
      setTimeout(() => {
        const cardElement = document.querySelector(`[data-testid="card-${bestMatch.card.id}"]`);
        if (cardElement) {
          cardElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
      
      // Remove highlight after a few seconds
      setTimeout(() => {
        setHighlightedCardId(null);
      }, 4000);
    } else {
      // No match found - add as new card to first list (To Do)
      const toDoList = currentBoard.lists.find(l => l.name.toLowerCase() === "to do");
      if (toDoList) {
        handleAddCard(toDoList.id, thought);
      } else if (currentBoard.lists[0]) {
        handleAddCard(currentBoard.lists[0].id, thought);
      }
    }
    
    setShowResetModal(false);
    
    // Trigger warm fog effect briefly after reset
    setRecentlyReset(true);
    setTimeout(() => setRecentlyReset(false), 8000); // Fade back after 8s
  };

  if (!data || isLoadingCloud) {
    return (
      <div className="min-h-screen bg-[var(--board-fog)] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-pulse mb-4">
            <LogoIcon size={48} />
          </div>
          <p className="text-[#A1A1AA]">
            {isLoadingCloud ? "Syncing your threads..." : "Loading..."}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen flex flex-col transition-colors duration-[3000ms] ease-out"
      style={{ backgroundColor: recentlyReset ? 'var(--board-fog-warm)' : 'var(--board-fog)' }}
    >
      {/* Save Status Indicator */}
      <SaveStatusIndicator />
      
      {/* Header */}
      <header className="bg-white border-b border-stone-100 px-3 md:px-6 py-2 md:py-3 sticky top-0 z-40">
        <div className="flex items-center justify-between">
          {/* Left: Logo with HOME label */}
          <Link 
            to="/" 
            className="flex flex-col items-center hover:opacity-80 transition-opacity flex-shrink-0"
            data-testid="app-logo-link"
          >
            <LogoIcon size={28} />
            <span className="text-[9px] font-medium text-[#A1A1AA] tracking-wide -mt-0.5">HOME</span>
          </Link>

          {isDemo && (
            <span className="bg-[#E0F7FA] text-[#0CBCBF] text-xs font-medium px-2 py-0.5 rounded-full ml-2">
              Demo
            </span>
          )}

          {/* Center: Search - extra left margin on mobile for iOS */}
          <div className={`flex-1 ml-4 sm:mx-3 mr-3 relative z-50 transition-all duration-200 ${searchQuery ? 'max-w-xl' : 'max-w-md'}`}>
            <AnimatePresence mode="wait">
              {isSearchOpen ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ duration: 0.15 }}
                  className="relative"
                >
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 z-10" />
                  <input
                    ref={searchInputRef}
                    type="text"
                    value={searchQuery}
                    onChange={(e) => {
                      const query = e.target.value;
                      setSearchQuery(query);
                      
                      // Auto-scroll to first matching card after a short delay
                      if (query.trim()) {
                        setTimeout(() => {
                          const matchingCard = document.querySelector('[data-testid^="card-"]');
                          if (matchingCard) {
                            matchingCard.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
                          }
                        }, 200);
                      }
                    }}
                    placeholder="Search tasks..."
                    className="w-full pl-9 pr-8 py-2 text-sm text-[#27272A] bg-white border border-stone-200 rounded-full outline-none focus:border-[#0CBCBF] focus:ring-1 focus:ring-[#0CBCBF]/20 transition-all"
                    style={{ fontSize: '16px' }} // Prevents iOS zoom on focus
                    data-testid="search-input"
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="off"
                    spellCheck="false"
                    onKeyDown={(e) => {
                      if (e.key === 'Escape') {
                        setSearchQuery("");
                        setIsSearchOpen(false);
                      }
                    }}
                  />
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery("")}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600 z-10"
                    >
                      <X size={14} />
                    </button>
                  )}
                </motion.div>
              ) : (
                <motion.button
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  onClick={() => {
                    setIsSearchOpen(true);
                    setTimeout(() => searchInputRef.current?.focus(), 100);
                  }}
                  className="flex items-center gap-2 p-2 sm:px-4 sm:py-2 text-sm text-stone-400 bg-stone-100/50 hover:bg-stone-100 rounded-full transition-colors"
                  data-testid="search-button"
                >
                  <Search size={16} />
                  <span className="hidden sm:inline text-sm">Search...</span>
                </motion.button>
              )}
            </AnimatePresence>
          </div>

          {/* Daily Focus toggle - hides non-Important tasks in To Do/Done */}
          <button
            onClick={() => setDailyFocus(!dailyFocus)}
            className={`flex items-center gap-1.5 px-2 sm:px-3 py-1.5 rounded-full text-xs font-medium transition-all mr-2 whitespace-nowrap ${
              dailyFocus
                ? 'bg-amber-100 text-amber-700 hover:bg-amber-200'
                : 'bg-stone-100/50 text-stone-500 hover:bg-stone-100'
            }`}
            title={dailyFocus ? "Showing today's focus (Important + Doing)" : "Focus on today"}
            data-testid="daily-focus-toggle"
          >
            {/* Label sits on the LEFT of the asterisk. When focus is off on
                mobile, hide the "Focus mode" label so the pill stays compact.
                When focus is ON, always show "Today" — including on iOS — so
                the user can see the state changed. */}
            {dailyFocus ? (
              <span>Today</span>
            ) : (
              <span className="hidden sm:inline">Focus mode</span>
            )}
            <span
              className="text-[15px] leading-none font-bold"
              style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}
            >
              *
            </span>
          </button>

          {/* Right: Sign in/out + Flame + Menu */}
          <div className="flex items-center gap-3.5">
            {/* Sign in / Log out button */}
            {!isDemo && (
              isAuthenticated ? (
                <button
                  onClick={signOut}
                  className="flex items-center gap-1 px-2 py-1 text-[11px] font-medium text-stone-500 bg-stone-100 hover:bg-stone-200 hover:text-stone-700 rounded-full transition-colors"
                  data-testid="log-out-button"
                >
                  <LogOut size={11} />
                  <span>Log out</span>
                </button>
              ) : (
                <button
                  onClick={signIn}
                  className="flex items-center gap-1.5 px-2 py-1 text-[12px] font-medium text-[#0CBCBF] bg-[#E0F7FA] hover:bg-[#B2EBF2] rounded-full transition-colors"
                  data-testid="sign-in-button"
                >
                  <Cloud size={11} />
                  <span>Sign in</span>
                </button>
              )
            )}

            {/* Fire Counter Badge */}
            <StreakBadge 
              streak={currentStreak}
              totalCompleted={totalCompleted}
              cycleCount={cycleCount}
              onClick={() => setShowStreakPanel(true)}
              justCompleted={justCompletedTask}
              particlesActive={showCelebration}
            />
            
            {/* Hamburger Menu */}
            <div className="relative" ref={menuRef}>
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                data-testid="hamburger-menu-button"
                className="p-1 text-stone-500 hover:text-[#0CBCBF] hover:bg-[#E0F7FA] rounded-full transition-colors"
                aria-label="Menu"
              >
                <Menu size={16} />
              </button>
              
              <AnimatePresence>
                {isMenuOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -8, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -8, scale: 0.95 }}
                    transition={{ duration: 0.15 }}
                    className="absolute right-0 top-full mt-2 w-56 bg-white rounded-xl shadow-lg border border-stone-100 overflow-hidden z-50"
                    data-testid="hamburger-menu-dropdown"
                  >
                    {/* Cloud sync status */}
                    {isAuthenticated && !isDemo && (
                      <div className="px-4 py-2.5 border-b border-stone-50 flex items-center gap-2.5 text-xs">
                        {syncStatus === 'syncing' && (
                          <><Cloud size={14} className="text-[#3DC4D0] animate-pulse" /><span className="text-[#3DC4D0]">Syncing...</span></>
                        )}
                        {syncStatus === 'synced' && (
                          <><Cloud size={14} className="text-green-500" /><span className="text-green-500">Saved to cloud</span></>
                        )}
                        {syncStatus === 'error' && (
                          <><CloudOff size={14} className="text-red-400" /><span className="text-red-400">Sync error</span></>
                        )}
                        {syncStatus === 'idle' && (
                          <><Cloud size={14} className="text-stone-400" /><span className="text-stone-400">Cloud connected</span></>
                        )}
                      </div>
                    )}

                    {/* Add to Home Screen — iOS Safari instructions or native prompt */}
                    {canInstall ? (
                      <button
                        onClick={handleMenuInstall}
                        className="w-full flex items-center gap-3 px-4 py-3 text-sm text-[#27272A] hover:bg-[#E8F5E9] transition-colors"
                        data-testid="menu-install-button"
                      >
                        <Home size={16} className="text-[#4CAF50]" />
                        <span>Add to Home Screen</span>
                      </button>
                    ) : /iPad|iPhone|iPod/.test(navigator.userAgent) ? (
                      <div
                        className="px-4 py-3 text-sm border-b border-stone-50"
                        data-testid="menu-ios-install-hint"
                      >
                        <div className="flex items-center gap-3 text-[#27272A] mb-1.5">
                          <Home size={16} className="text-[#4CAF50] flex-shrink-0" />
                          <span className="font-medium">Add to Home Screen</span>
                        </div>
                        <p className="text-xs text-[#A1A1AA] leading-relaxed ml-7">
                          Tap the <span className="inline-block px-1 py-0.5 bg-stone-100 rounded text-[#52525B] font-medium">Share</span> button in Safari, then choose <span className="text-[#52525B] font-medium">"Add to Home Screen"</span>
                        </p>
                      </div>
                    ) : null}
                    
                    {/* Settings */}
                    <button
                      onClick={() => {
                        setShowSettings(true);
                        setIsMenuOpen(false);
                      }}
                      className="w-full flex items-center gap-3 px-4 py-3 text-sm text-[#27272A] hover:bg-stone-50 transition-colors"
                      data-testid="menu-settings-button"
                    >
                      <Settings size={16} className="text-stone-500" />
                      <span>Settings</span>
                    </button>
                    
                    {/* Print View */}
                    <button
                      onClick={() => {
                        setShowPrintView(true);
                        setIsMenuOpen(false);
                      }}
                      className="w-full flex items-center gap-3 px-4 py-3 text-sm text-[#27272A] hover:bg-stone-50 transition-colors"
                      data-testid="menu-print-button"
                    >
                      <Printer size={16} className="text-stone-500" />
                      <span>Print View</span>
                    </button>
                    
                    {/* Share this app */}
                    <button
                      onClick={() => {
                        handleShareApp();
                        setIsMenuOpen(false);
                      }}
                      className="w-full flex items-center gap-3 px-4 py-3 text-sm text-[#27272A] hover:bg-stone-50 transition-colors"
                      data-testid="menu-share-app-button"
                    >
                      <Share2 size={16} className="text-[#0CBCBF]" />
                      <span>Share this app</span>
                    </button>
                    
                    {/* Sign in / Sign out */}
                    {!isDemo && (
                      isAuthenticated ? (
                        <button
                          onClick={() => {
                            signOut();
                            setIsMenuOpen(false);
                          }}
                          className="w-full flex items-center gap-3 px-4 py-3 text-sm text-[#27272A] hover:bg-stone-50 transition-colors border-t border-stone-50"
                          data-testid="menu-logout-button"
                        >
                          <LogOut size={16} className="text-stone-500" />
                          <span>Log out</span>
                        </button>
                      ) : (
                        <button
                          onClick={() => {
                            signIn();
                            setIsMenuOpen(false);
                          }}
                          className="w-full flex items-center gap-3 px-4 py-3 text-sm text-[#0CBCBF] hover:bg-[#E0F7FA] transition-colors border-t border-stone-50"
                          data-testid="menu-signin-button"
                        >
                          <Cloud size={16} className="text-[#0CBCBF]" />
                          <span>Sign in to sync</span>
                        </button>
                      )
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </header>

      {/* Pause State — gentle "Welcome back" overlay */}
      <AnimatePresence>
        {showPauseState && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5, exit: { duration: 0.6 } }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/10 pointer-events-none"
            data-testid="pause-state"
          >
            <motion.p
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.4, delay: 0.1 }}
              className="text-xl text-[#A1A1AA] font-light tracking-wide"
            >
              Welcome back.
            </motion.p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Orientation Strip — "You were on: [task]" */}
      <AnimatePresence>
        {showOrientationStrip && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.4, ease: 'easeOut' }}
            className="bg-white/80 backdrop-blur-sm border-b border-stone-100 px-4 md:px-6 py-3 text-center"
            data-testid="orientation-strip"
          >
            {orientationText ? (
              <p className="text-sm text-[#A1A1AA] tracking-wide font-light">
                You were on: <span className="text-[#52525B] font-normal">{orientationText}</span>
              </p>
            ) : (
              <p className="text-sm text-[#A1A1AA] tracking-wide font-light">
                Start a task to create your current thread.
              </p>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Dim overlay when finding thread */}
      <AnimatePresence>
        {showDimOverlay && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3, exit: { duration: 1 } }}
            className="fixed inset-0 bg-black/15 z-30 pointer-events-none"
            data-testid="dim-overlay"
          />
        )}
      </AnimatePresence>

      {/* Board */}
      <main 
        ref={boardRef}
        className="flex-1 overflow-x-auto p-4 pb-44 md:p-6 md:pb-6 snap-x snap-mandatory scroll-smooth relative"
        data-board-area="true"
        onClick={(e) => {
          // Clear search when clicking on empty areas
          if (searchQuery) {
            const clickedElement = e.target;
            // Check various dismiss conditions
            const isBoardBackground = clickedElement.hasAttribute('data-board-area');
            const isDismissArea = clickedElement.hasAttribute('data-dismiss-search');
            const isNoMatchingText = clickedElement.textContent?.includes('No matching tasks');
            const isFlexContainer = clickedElement.classList.contains('flex') && 
                                   clickedElement.classList.contains('gap-4');
            
            if (isBoardBackground || isDismissArea || isNoMatchingText || isFlexContainer) {
              setSearchQuery("");
              setIsSearchOpen(false);
            }
          }
        }}
      >
        {/* Focus Vibes (Return Loop) - REMOVED, moved to roadmap */}

        {/* Single DndContext for all drag operations */}
        <DndContext
          sensors={cardSensors}
          collisionDetection={closestCorners}
          onDragStart={(event) => {
            const { active } = event;
            
            // Check if this is a column drag (column IDs start with 'list-')
            if (active.data?.current?.type === 'column') {
              setActiveColumn(active.data.current.list);
              setActiveCard(null);
            } else {
              // Card drag
              const list = findListByCardId(active.id);
              if (list) {
                const card = list.cards.find(c => c.id === active.id);
                setActiveCard(card);
                setActiveColumn(null);
              }
            }
          }}
          onDragEnd={(event) => {
            const { active, over } = event;
            
            // Column drag
            if (active.data?.current?.type === 'column') {
              setActiveColumn(null);
              
              if (!over || active.id === over.id) return;
              
              // Find the target column (over might be a column or a card inside a column)
              let targetColumnId = over.id;
              
              // Check if over.id is a card - if so, find its parent column
              const overColumn = currentBoard.lists.find(l => l.id === over.id);
              if (!overColumn) {
                // over.id might be a card, find which column it belongs to
                for (const list of currentBoard.lists) {
                  if (list.cards.some(c => c.id === over.id)) {
                    targetColumnId = list.id;
                    break;
                  }
                }
              }
              
              const oldIndex = currentBoard.lists.findIndex(l => l.id === active.id);
              const newIndex = currentBoard.lists.findIndex(l => l.id === targetColumnId);
              
              if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
                const newLists = arrayMove(currentBoard.lists, oldIndex, newIndex);
                setData(prevData => ({
                  ...prevData,
                  boards: prevData.boards.map(board =>
                    board.id === currentBoard.id
                      ? { ...board, lists: newLists }
                      : board
                  )
                }));
              }
              return;
            }
            
            // Card drag
            handleDragEnd(event);
          }}
        >
          <SortableContext
            items={currentBoard?.lists.map(l => l.id) || []}
            strategy={horizontalListSortingStrategy}
          >
              <div className="flex gap-4 items-start min-h-full">
                {currentBoard?.lists.map((list, index) => {
                  // Determine header content based on list
                  let headerContent = null;
                  let footerContent = null;

                  // Inline mobile-only Card Action Guide - rendered at the
                  // bottom of every column so it sits inside the scroll (not
                  // superimposed). Desktop keeps a single copy under TO DO.
                  const mobileGuide = showActionGuide ? (
                    <div className="md:hidden">
                      <ActionGuide autoCollapsed={hasInteracted} />
                    </div>
                  ) : null;

                  if (list.name.toLowerCase() === "to do" && !isDemo) {
                    headerContent = (
                      <>
                        <DailySystemTask enabled={dailyReminderEnabled} />
                      </>
                    );
                    // Desktop: inline below TO DO cards. Mobile: same slot too
                    // (this keeps the TO DO column's scroll-bottom experience).
                    if (showActionGuide) {
                      footerContent = (
                        <ActionGuide autoCollapsed={hasInteracted} />
                      );
                    }
                  } else if ((list.name.toLowerCase() === "doing" || list.name.toLowerCase() === "in progress") && !isDemo) {
                    headerContent = (
                      <DailyMovementTask enabled={dailyReminderEnabled} />
                    );
                    // Show share app button below Doing column (dismissible)
                    if (showShareAppButton) {
                      footerContent = (
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -10 }}
                          className="mt-3 relative"
                        >
                          <button
                            onClick={handleShareApp}
                            className="w-full py-2.5 px-4 bg-gradient-to-r from-[#3DC4D0]/10 to-[#0CBCBF]/15 hover:from-[#3DC4D0]/20 hover:to-[#0CBCBF]/25 text-stone-600 text-sm font-medium rounded-xl transition-all flex items-center justify-center gap-2"
                            style={{
                              boxShadow: '0 2px 8px rgba(61, 196, 208, 0.15), 0 1px 3px rgba(0, 0, 0, 0.08)'
                            }}
                            data-testid="share-app-button"
                          >
                            <Share2 size={14} className="text-[#0CBCBF]" />
                            Share this app
                          </button>
                          <button
                            onClick={dismissShareAppButton}
                            className="absolute -top-1 -right-1 w-5 h-5 bg-white rounded-full shadow-sm flex items-center justify-center text-stone-400 hover:text-stone-600 transition-colors"
                            aria-label="Dismiss"
                          >
                            <X size={12} />
                          </button>
                        </motion.div>
                      );
                    }
                    // Mobile: also append the guide below the share button so
                    // scrolling to the bottom of Doing shows it in-flow.
                    if (mobileGuide) {
                      footerContent = (
                        <>
                          {footerContent}
                          {mobileGuide}
                        </>
                      );
                    }
                  } else if (list.name.toLowerCase() === "done" && !isDemo) {
                    headerContent = (
                      <CompletionAcknowledgment 
                        completedCount={sessionCompletedCount}
                        sessionMilestonesHit={sessionMilestonesHit}
                      />
                    );
                    // Mobile: put the guide at the bottom of Done too.
                    footerContent = mobileGuide;
                  }
                  
                  return (
                    <SortableColumn 
                      key={list.id} 
                      list={list}
                      isDragging={activeColumn?.id === list.id}
                      columnIndex={index}
                    >
                      {({ listeners, attributes }) => (
                        <ListColumn
                          list={list}
                          onAddCard={handleAddCard}
                          onEditCard={handleEditCard}
                          onDeleteCard={handleDeleteCard}
                          onMarkDone={handleMarkDone}
                          onMoveToList={handleMoveToList}
                          onToggleCritical={handleToggleCritical}
                          onToggleNudge={handleToggleNudge}
                          onShareTask={handleShareTask}
                          onRenameList={handleRenameList}
                          onDeleteList={handleDeleteList}
                          highlightedCardId={highlightedCardId}
                          headerContent={headerContent}
                          footerContent={footerContent}
                          dragListeners={listeners}
                          dragAttributes={attributes}
                          searchQuery={searchQuery}
                          dailyFocus={dailyFocus}
                          showEditHint={showEditHint}
                          onDismissEditHint={dismissEditHint}
                          nudgeSupported={EMAIL_NUDGES_ENABLED && (!!user || !!nudgeSystem?.isSupported)}
                          onLockScreenPin={handleLockScreenPin}
                          lockScreenTaskId={lockScreen.status.enabled ? lockScreen.status.taskId : null}
                        />
                      )}
                    </SortableColumn>
                  );
                })}

              {/* Add List */}
              <div className="snap-center flex-shrink-0">
            {isAddingList ? (
              <div 
                className="rounded-2xl p-4 w-72 relative overflow-hidden"
                style={{
                  backgroundColor: 'var(--column-todo)',
                  boxShadow: 'var(--elevation-1)',
                }}
              >
                {/* Surface depth */}
                <div 
                  className="absolute inset-0 pointer-events-none rounded-2xl"
                  style={{ background: 'var(--card-surface-light)' }}
                />
                <input
                  type="text"
                  value={newListName}
                  onChange={(e) => setNewListName(e.target.value)}
                  placeholder="List name..."
                  className="relative z-10 w-full bg-white rounded-xl px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-[#F58227]/30"
                  style={{ boxShadow: 'var(--elevation-1)' }}
                  autoFocus
                  onKeyDown={(e) => e.key === "Enter" && handleAddList()}
                />
                <div className="flex gap-2 mt-3 relative z-10">
                  <button
                    onClick={handleAddList}
                    className="flex-1 bg-[#F58227] text-white rounded-lg py-2 text-sm font-medium hover:bg-[#D96D19] transition-colors"
                    style={{ boxShadow: 'var(--elevation-2-accent)' }}
                  >
                    Add List
                  </button>
                  <button
                    onClick={() => { setIsAddingList(false); setNewListName(""); }}
                    className="px-3 text-stone-500 hover:text-stone-700"
                  >
                    <X size={20} />
                  </button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => setIsAddingList(true)}
                className="bg-white/60 hover:bg-white rounded-2xl p-4 w-72 flex items-center gap-2 text-stone-500 hover:text-[#F58227] transition-all"
                style={{
                  boxShadow: 'inset 0 0 0 2px rgba(214, 211, 209, 0.4)',
                }}
                data-testid="add-list-button"
              >
                <Plus size={20} />
                Add another list
              </button>
            )}
            </div>
              </div>

              {/* Drag Overlay */}
              <DragOverlay>
                {activeCard ? (
                  <div 
                    className="bg-white rounded-xl p-4 border border-stone-200 w-64 rotate-3"
                    style={{ boxShadow: '0 12px 32px rgba(0, 0, 0, 0.15)' }}
                  >
                    <p className="text-sm text-[#27272A]">{activeCard.content}</p>
                  </div>
                ) : activeColumn ? (
                  <div 
                    className="bg-[#F5F5F4] rounded-2xl p-4 w-72 opacity-90 rotate-2"
                    style={{ boxShadow: '0 16px 40px rgba(0, 0, 0, 0.18)' }}
                  >
                    <div className="flex items-center mb-3">
                      <div className="grid grid-cols-2 gap-[3px] mr-3 text-stone-400 p-1">
                        <div className="w-1 h-1 rounded-full bg-current" />
                        <div className="w-1 h-1 rounded-full bg-current" />
                        <div className="w-1 h-1 rounded-full bg-current" />
                        <div className="w-1 h-1 rounded-full bg-current" />
                        <div className="w-1 h-1 rounded-full bg-current" />
                        <div className="w-1 h-1 rounded-full bg-current" />
                      </div>
                      <h3 className="font-bold text-[#1A1A1C] text-lg uppercase tracking-wide">{activeColumn.name}</h3>
                    </div>
                    <div className="space-y-2">
                      {activeColumn.cards.slice(0, 3).map(card => (
                        <div key={card.id} className="bg-white rounded-lg p-2 text-xs text-stone-500 truncate">
                          {card.content}
                        </div>
                      ))}
                      {activeColumn.cards.length > 3 && (
                        <div className="text-xs text-stone-400 text-center">
                          +{activeColumn.cards.length - 3} more
                        </div>
                      )}
                    </div>
                  </div>
                ) : null}
              </DragOverlay>
          </SortableContext>
        </DndContext>
      </main>

      {/* Reset Modal */}
      <AnimatePresence>
        {showResetModal && (
          <ResetModal 
            onComplete={handleResetComplete} 
            onClose={() => setShowResetModal(false)}
            criticalTasks={getCriticalTasks()}
          />
        )}
      </AnimatePresence>

      {/* Card Edit Modal */}
      <AnimatePresence>
        {editingCard && (
          <CardEditModal
            card={editingCard}
            onSave={handleSaveCard}
            onClose={() => setEditingCard(null)}
            onShare={(card) => {
              setShareTask(card);
              setShowShareModal(true);
            }}
            onDelete={(cardId) => {
              // Find the list containing this card
              const board = data?.boards?.[0];
              if (board) {
                const list = board.lists.find(l => l.cards.some(c => c.id === cardId));
                if (list) {
                  handleDeleteCard(cardId, list.id);
                }
              }
              // Close the edit modal after deletion
              setEditingCard(null);
            }}
            nudgeSystem={nudgeSystem}
            userId={user?.id}
            onShowNudgePermission={handleShowNudgePermission}
            onOpenInstallHelp={() => setIsMenuOpen(true)}
            onOpenLockScreen={handleLockScreenPin}
            isLockScreenFocus={lockScreen.status.enabled && lockScreen.status.taskId === editingCard.id}
            isInDoingList={(() => {
              const list = data?.boards?.[0]?.lists?.find(l => l.cards.some(c => c.id === editingCard.id));
              const name = list?.name?.toLowerCase();
              return name === 'doing' || name === 'in progress' || name === 'to do';
            })()}
          />
        )}
      </AnimatePresence>
      
      {/* Nudge Permission Modal */}
      <AnimatePresence>
        {showNudgePermissionModal && (
          <NudgePermissionModal
            isOpen={showNudgePermissionModal}
            onClose={() => {
              setShowNudgePermissionModal(false);
              setPendingNudgeTask(null);
            }}
            onPermissionGranted={handleNudgePermissionGranted}
            taskName={pendingNudgeTask?.name}
          />
        )}
      </AnimatePresence>

      {/* Lock Screen Focus modal */}
      <LockScreenFocusModal
        open={showLockScreenModal}
        onClose={() => { setShowLockScreenModal(false); setLockScreenPendingTask(null); }}
        taskId={lockScreenPendingTask?.id}
        taskName={lockScreenPendingTask?.name}
        currentFocus={lockScreen.status.enabled ? { taskId: lockScreen.status.taskId, taskName: lockScreen.status.taskName, intervalMinutes: lockScreen.status.intervalMinutes } : null}
        isAuthenticated={isAuthenticated}
        onSignIn={signIn}
        onEnable={handleLockScreenEnable}
        subscribeToPush={nudgeSystem?.subscribeToPush}
        permissionStatus={nudgeSystem?.permissionStatus}
        isIOSSafariNoPWA={nudgeSystem?.isIOSSafariNoPWA}
        isSupported={nudgeSystem?.isSupported}
      />

      {/* Settings Panel */}
      <AnimatePresence>
        {showSettings && (
          <SettingsPanel
            isOpen={showSettings}
            onClose={() => setShowSettings(false)}
          />
        )}
      </AnimatePresence>

      {/* Completion Celebration */}
      <CompletionCelebration
        isActive={showCelebration}
        onComplete={() => setShowCelebration(false)}
        origin={celebrationOrigin}
      />

      {/* Fire Counter Panel */}
      <AnimatePresence>
        {showStreakPanel && (
          <StreakPanel
            isOpen={showStreakPanel}
            onClose={() => setShowStreakPanel(false)}
            currentStreak={currentStreak}
            longestStreak={longestStreak}
            totalCompleted={totalCompleted}
          />
        )}
      </AnimatePresence>

      {/* Milestone Celebration */}
      <AnimatePresence>
        {newMilestone && (
          <MilestoneCelebration 
            milestone={newMilestone} 
            onDismiss={dismissMilestone} 
          />
        )}
      </AnimatePresence>

      {/* Early Momentum Modal at 10 tasks */}
      <EarlyMomentumModal 
        isOpen={showEarlyMomentumModal} 
        onClose={() => {
          setShowEarlyMomentumModal(false);
          // Show share panel after celebration settles (1.5s delay)
          if (!sharedMilestones.has(10)) {
            setTimeout(() => {
              setMilestoneShareValue(10);
              setShowMilestoneSharePanel(true);
              setSharedMilestones(prev => new Set([...prev, 10]));
            }, 1500);
          }
        }} 
      />

      {/* Quarter Mark Modal at 25 tasks */}
      <QuarterMarkModal 
        isOpen={showQuarterMarkModal} 
        onClose={() => {
          setShowQuarterMarkModal(false);
          if (!sharedMilestones.has(25)) {
            setTimeout(() => {
              setMilestoneShareValue(25);
              setShowMilestoneSharePanel(true);
              setSharedMilestones(prev => new Set([...prev, 25]));
            }, 1500);
          }
        }} 
      />

      {/* Goal Completion Modal at 50 tasks */}
      <GoalCompletionModal 
        isOpen={showGoalCompletionModal} 
        onClose={() => {
          setShowGoalCompletionModal(false);
          if (!sharedMilestones.has(50)) {
            setTimeout(() => {
              setMilestoneShareValue(50);
              setShowMilestoneSharePanel(true);
              setSharedMilestones(prev => new Set([...prev, 50]));
            }, 1500);
          }
        }} 
      />

      {/* Century Milestone Modal (100, 200, 300... 900) */}
      <CycleCompletionModal 
        isOpen={showCycleCompletionModal} 
        onClose={() => {
          setShowCycleCompletionModal(false);
          // Show share panel after milestone
          if (!sharedMilestones.has(currentStreak)) {
            setTimeout(() => {
              setMilestoneShareValue(currentStreak);
              setShowMilestoneSharePanel(true);
              setSharedMilestones(prev => new Set([...prev, currentStreak]));
            }, 1500);
          }
        }}
        onCycleReset={null}
        cycleCount={Math.floor(currentStreak / 100)}
        totalCompleted={totalCompleted}
      />

      {/* Post-Milestone Share Panel */}
      <MilestoneSharePanel
        isOpen={showMilestoneSharePanel}
        onClose={() => setShowMilestoneSharePanel(false)}
        milestone={milestoneShareValue}
      />

      {/* Print View Modal */}
      <PrintView 
        isOpen={showPrintView}
        onClose={() => setShowPrintView(false)}
        lists={data}
      />

      {/* Share Task Modal */}
      <ShareTaskModal 
        isOpen={showShareModal}
        onClose={() => {
          setShowShareModal(false);
          setShareTask(null);
        }}
        task={shareTask}
        userName={user?.name || 'Someone'}
      />

      {/* Share App Modal (Desktop) */}
      <AnimatePresence>
        {showShareAppModal && (
          <ShareAppModal
            onClose={() => setShowShareAppModal(false)}
            onCopyLink={copyAppLink}
            onEmail={shareViaEmail}
            onTwitter={shareViaTwitter}
            onLinkedIn={shareViaLinkedIn}
            onFacebook={shareViaFacebook}
          />
        )}
      </AnimatePresence>

      {/* Find Thread Ritual - Breathing animation */}
      <AnimatePresence>
        {showFindThreadRitual && (
          <FindThreadRitual
            onComplete={handleRitualComplete}
            taskName={ritualTaskName}
          />
        )}
      </AnimatePresence>

      {/* Sync Invitation - gentle prompt for cloud backup */}
      {!isDemo && <SyncInvitation />}
      
      {/* Sign In Reminder - floating top reminder for unauthenticated users */}
      {!isDemo && <SignInReminder />}

      {/* PWA Install Prompt - gentle nudge for mobile users */}
      {!isDemo && <InstallPrompt />}

      {/* Reminder Opt-In - ask if user wants weekly reminders */}
      {showReminderOptIn && (
        <ReminderOptIn 
          onClose={handleReminderOptInClose}
          userId={user?.user_id}
          userEmail={user?.email}
        />
      )}

      {/* One-time drag hint for new users */}
      {!isDemo && <DragHint />}

      {/* Mobile Fixed Footer Bar — hidden while the Edit Card modal is open
          so it can't cover the modal's Save/Cancel action bar on short viewports */}
      {!editingCard && (
        <MobileFooter
          onFindMyThread={handleFindMyThread}
          onQuickAddOpen={() => {
            setIsQuickAddOpen(true);
            setTimeout(() => quickAddInputRef.current?.focus(), 100);
          }}
          isQuickAddOpen={isQuickAddOpen}
          threadDriftGlow={threadDriftGlow}
          totalColumns={currentBoard?.lists?.length || 0}
          boardRef={boardRef}
        />
      )}

      {/* Desktop Footer Buttons */}
      <DesktopFooterButtons
        onFindMyThread={handleFindMyThread}
        onQuickAddOpen={() => {
          setIsQuickAddOpen(true);
          setTimeout(() => quickAddInputRef.current?.focus(), 100);
        }}
        isQuickAddOpen={isQuickAddOpen}
        threadDriftGlow={threadDriftGlow}
      />

      {/* Quick Add Modal */}
      <AnimatePresence>
        {isQuickAddOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => {
                setIsQuickAddOpen(false);
                setQuickAddText("");
              }}
              className="fixed inset-0 bg-black/20 backdrop-blur-sm z-50"
            />
            
            {/* Quick Add Input */}
            <motion.div
              initial={{ opacity: 0, y: 50, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 50, scale: 0.95 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="fixed bottom-6 left-4 right-4 sm:left-auto sm:right-6 sm:w-96 bg-white rounded-2xl shadow-2xl p-4 z-50"
            >
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 bg-[#F58227] rounded-full flex items-center justify-center">
                  <Plus size={18} className="text-white" />
                </div>
                <span className="font-medium text-[#27272A]">Quick Add to To Do</span>
              </div>
              
              <textarea
                ref={quickAddInputRef}
                value={quickAddText}
                onChange={(e) => setQuickAddText(e.target.value)}
                placeholder="What's on your mind?"
                className="w-full bg-stone-50 rounded-xl p-3 text-base border border-stone-200 outline-none focus:border-[#F58227] focus:ring-2 focus:ring-[#F58227]/10 resize-none transition-all"
                rows={3}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleQuickAdd();
                  }
                  if (e.key === 'Escape') {
                    setIsQuickAddOpen(false);
                    setQuickAddText("");
                  }
                }}
                data-testid="quick-add-input"
              />
              
              <div className="flex items-center justify-between mt-3">
                <span className="text-xs text-stone-400">Press Enter to add</span>
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setIsQuickAddOpen(false);
                      setQuickAddText("");
                    }}
                    className="px-4 py-2 text-sm text-stone-500 hover:text-stone-700 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleQuickAdd}
                    disabled={!quickAddText.trim()}
                    className="px-4 py-2 text-sm font-medium text-white bg-[#F58227] hover:bg-[#D96D19] disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors"
                    data-testid="quick-add-submit"
                  >
                    Add Task
                  </button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}
